import TextInput from '../../TextInput';
import RichTextEditor from '../../RichTextEditor.jsx';
import OptionToggle from '../../OptionToggle.jsx';
import React from 'react';
import CollapsiblePanel from '~/components/CollapsiblePanel';
import { useFlowQuery } from '~/lib/Queries';
import { ObjectTypes } from '~/lib/ObjectTypes.js';
import TaggableInput from '~/components/TaggableInput.jsx';
import ErrorMessage from '~/components/ErrorMessage';
import { stripTags } from '~/lib/Utils.js';
import Loading from '~/components/Loading';

const RedirectUriInput = ({ value, setValue, formId }) => {
    const { data: flowData, isLoading: isLoadingFlowData } = useFlowQuery(
        ObjectTypes.FORM,
        formId,
    );

    const strippedValue = stripTags(value);
    const showHttpsRequiredError =
        strippedValue?.length >= 8 && !strippedValue.startsWith('https://');

    if (isLoadingFlowData) {
        return (
            <div className="mt-6">
                <div className="panel-label">Redirect URL</div>
                <div className="p-4 panel relative">
                    <Loading />
                </div>
            </div>
        );
    }

    return (
        <div className="mt-6">
            <div className="panel-label">Redirect URL</div>
            <div className="p-4 panel relative">
                <TaggableInput
                    showInsertInline={true}
                    className="pr-24"
                    schema={flowData?.schema || []}
                    initialValue={value}
                    onChange={setValue}
                    appendAfterInsert="&"
                    placeholder="https://your-website.com"
                    noLinking={true}
                />
                {showHttpsRequiredError && (
                    <ErrorMessage>
                        The redirect URL must start with <code>https://</code>
                    </ErrorMessage>
                )}
                <div className="text-sm text-gray-500 mt-1">
                    After successfully submitting this form clients will be
                    redirected to this website.
                </div>
            </div>
        </div>
    );
};

const EditThankYouForm = ({
    settings,
    setSettings,
    hasScheduling,
    formDetails,
}) => {
    return (
        <div>
            <TextInput
                label="Title"
                inputClassName="w-full"
                value={settings?.title || ''}
                onChange={value => setSettings({ ...settings, title: value })}
            />
            <div className="input-label mt-4">Content</div>
            <RichTextEditor
                initialValue={settings?.description || ''}
                onChange={value =>
                    setSettings({ ...settings, description: value })
                }
            />
            <div className="mt-4 p-4 panel space-y-4">
                <OptionToggle
                    value={settings?.hideCheckmark}
                    onChange={value =>
                        setSettings({ ...settings, hideCheckmark: value })
                    }
                    title="Hide Checkmark"
                    description="Hide the checkmark image"
                />
                {!!hasScheduling && (
                    <>
                        <OptionToggle
                            value={settings?.hideCancel}
                            onChange={value =>
                                setSettings({ ...settings, hideCancel: value })
                            }
                            title="Disable Canceling"
                            description="Don't allow clients to cancel appointments"
                        />
                        <OptionToggle
                            value={settings?.hideReschedule}
                            onChange={value =>
                                setSettings({
                                    ...settings,
                                    hideReschedule: value,
                                })
                            }
                            title="Disable Rescheduling"
                            description="Don't allow clients to reschedule appointments"
                        />

                        <OptionToggle
                            value={settings?.allowReschedulingResource}
                            onChange={value =>
                                setSettings({
                                    ...settings,
                                    allowReschedulingResource: value,
                                })
                            }
                            title="Allow Rescheduling to Different Person"
                            description="When rescheduling allow clients to change who they are booked with"
                        />

                        <OptionToggle
                            value={settings?.allowPayBalance}
                            onChange={value =>
                                setSettings({
                                    ...settings,
                                    allowPayBalance: value,
                                })
                            }
                            title="Allow Paying Balance"
                            description="Allow clients who just paid a deposit to pay the balance remaining"
                        />

                        <div>
                            <OptionToggle
                                value={settings?.showLimitsMessage}
                                onChange={value =>
                                    setSettings({
                                        ...settings,
                                        showLimitsMessage: value,
                                    })
                                }
                                title="Show Message When Unavailable"
                                description="Show a custom message when clients can no longer cancel or reschedule, such as when the scheduling limit has passed"
                            />
                            {!!settings?.showLimitsMessage && (
                                <div className="mt-4">
                                    <RichTextEditor
                                        initialValue={
                                            settings?.limitsMessage || ''
                                        }
                                        onChange={value =>
                                            setSettings({
                                                ...settings,
                                                limitsMessage: value,
                                            })
                                        }
                                        compactMenu={true}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <RedirectUriInput
                value={settings?.redirectUri}
                setValue={value =>
                    setSettings({
                        ...settings,
                        redirectUri: value,
                    })
                }
                formId={formDetails.form?.id}
            />
        </div>
    );
};

export default EditThankYouForm;
